import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, ChevronRight, AlertTriangle, CheckCircle2, Search, Anchor, RefreshCw } from 'lucide-react';
import { MathService } from '../services/mathService';

export const TruthDivergenceTest: React.FC = () => {
  const [limit, setLimit] = useState(12);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);

  const testResults = useMemo(() => {
    const results = [];
    let matches = 0;
    let divergences = 0;

    for (let i = 0; i <= limit; i++) {
      for (let j = 0; j <= limit; j++) {
        const mVal = MathService.miyakeLogic(i, j).result.toNumber();
        const sVal = MathService.standardLogic(i, j);
        const status = mVal === sVal ? 'MATCH' : 'DIVERGE';

        if (status === 'MATCH') matches++;
        else divergences++;

        if (i <= 6 && j <= 6) {
          results.push({ a: i, b: j, miyake: mVal, standard: sVal, status });
        }
      }
    }

    return { results, matches, divergences, total: (limit + 1) * (limit + 1) };
  }, [limit]);

  const runAnalysis = () => {
    setIsTestRunning(true);
    setTestCompleted(false);
    setTimeout(() => {
      setIsTestRunning(false);
      setTestCompleted(true);
    }, 1500);
  };

  return (
    <div id="truth-divergence-test" className="w-full bg-black border border-white/5 p-8 rounded-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Anchor className="w-4 h-4 text-lattice-cyan" />
            <h3 className="text-[10px] font-mono font-bold tracking-[0.3em] text-white/50 uppercase">
              Truth Barrier Analysis
            </h3>
          </div>
          <h2 className="text-3xl font-sans font-medium tracking-tighter text-white">
            Unified Truth Audit <span className="text-white/20 italic font-light ml-2">Correlated to Tree Ring Record</span>
          </h2>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <span className="text-[8px] font-mono text-white/30 uppercase tracking-widest mb-1">Time Anchor</span>
            <div className="bg-black border border-white/10 text-white font-mono text-xs px-3 py-2 w-32 focus:outline-none focus:border-lattice-cyan/50 transition-colors flex items-center justify-between">
              <span>14350 BP</span>
              <Anchor className="w-2.5 h-2.5 text-lattice-cyan" />
            </div>
          </div>
          <button 
            onClick={runAnalysis}
            className="h-10 px-6 bg-white text-black text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-lattice-cyan transition-all flex items-center gap-2 group"
          >
            {isTestRunning ? 'Verifying...' : 'Audit Information'}
            <RefreshCw className="w-3 h-3 group-hover:rotate-180 transition-transform duration-700" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Comparison Table */}
        <div className="space-y-4">
           <div className="grid grid-cols-4 pb-4 border-b border-white/5 text-[9px] font-mono font-bold text-white/30 uppercase tracking-widest">
              <div>Interaction</div>
              <div>Unified</div>
              <div>Standard</div>
              <div>Status</div>
           </div>
           
           <div className="space-y-[2px]">
             {testResults.results.map((row, idx) => (
               <motion.div 
                 key={`${row.a}-${row.b}`}
                 initial={{ opacity: 0, x: -10 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ delay: idx * 0.02 }}
                 className="grid grid-cols-4 py-3 border-b border-white/[0.02] text-xs font-mono group hover:bg-white/[0.02] transition-colors"
               >
                 <div className="text-white/40">{row.a} x {row.b}</div>
                 <div className={row.status === 'DIVERGE' ? 'text-lattice-cyan font-bold' : 'text-white'}>{row.miyake}</div>
                 <div className="text-white/60">{row.standard}</div>
                 <div className="flex items-center gap-2">
                   {row.status === 'MATCH' ? (
                     <CheckCircle2 className="w-3 h-3 text-white/20" />
                   ) : (
                     <AlertTriangle className="w-3 h-3 text-lattice-cyan" />
                   )}
                   <span className={row.status === 'MATCH' ? 'text-white/20' : 'text-lattice-cyan'}>
                     {row.status}
                   </span>
                 </div>
               </motion.div>
             ))}
           </div>
           <div className="pt-4 text-[8px] font-mono text-white/20 italic">
             Showing limited sample grid (up to 6x6) for visualization.
           </div>
        </div>

        {/* Stats and Findings */}
        <div className="bg-white/5 p-8 rounded-sm border border-white/5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-8">
              <Search className="w-4 h-4 text-white/40" />
              <h4 className="text-[10px] font-mono font-bold text-white/40 uppercase tracking-widest">Statistical Invariants</h4>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-12">
              <div>
                <div className="text-[8px] font-mono text-white/30 uppercase tracking-widest mb-1">Total Verified</div>
                <div className="text-2xl font-mono font-bold text-white tracking-tighter">
                   {isTestRunning ? '...' : testResults.total.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[8px] font-mono text-white/30 uppercase tracking-widest mb-1">Divergences Found</div>
                <div className="text-2xl font-mono font-bold text-lattice-cyan tracking-tighter">
                   {isTestRunning ? '...' : testResults.divergences.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[8px] font-mono text-white/30 uppercase tracking-widest mb-1">Match Consistency</div>
                <div className="text-2xl font-mono font-bold text-white tracking-tighter">
                   {isTestRunning ? '...' : `${((testResults.matches / testResults.total) * 100).toFixed(2)}%`}
                </div>
              </div>
              <div>
                <div className="text-[8px] font-mono text-white/30 uppercase tracking-widest mb-1">Anchor Drift</div>
                <div className="text-2xl font-mono font-bold text-lattice-cyan tracking-tighter">
                   {isTestRunning ? '...' : `${((testResults.divergences / testResults.total) * 100).toFixed(2)}%`}
                </div>
              </div>
            </div>

            <div className="space-y-6">
               <div className="p-4 bg-black/40 border-l-2 border-lattice-cyan/50">
                  <p className="text-xs leading-relaxed text-white/60 font-mono italic">
                    "The drift occurs at the Zero and One interactions. 1.0 logic deletes states that 2.0 Anchors."
                  </p>
               </div>
               
               <ul className="space-y-3">
                 {[
                   { label: 'Persistence Drift', detail: 'X * 0 results in state nullification (0) in 1.0 systems.' },
                   { label: 'Expansion Shift', detail: 'X * 1 results in static state (X) in 1.0, but manifests as Active Increment (X+1) in 2.0.' },
                   { label: 'Zero-Drift Resolution', detail: 'All divergent bits are recovered to the 14350 BP substrate.' }
                 ].map((item, idx) => (
                   <li key={idx} className="flex gap-4 items-start">
                     <span className="text-[10px] font-mono text-lattice-cyan mt-1">0{idx+1}</span>
                     <div>
                       <div className="text-[10px] font-mono font-bold text-white uppercase tracking-wider">{item.label}</div>
                       <div className="text-[10px] font-mono text-white/40 leading-relaxed">{item.detail}</div>
                     </div>
                   </li>
                 ))}
               </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5">
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-2">
                 <Shield className="w-3 h-3 text-lattice-cyan" />
                 <span className="text-[9px] font-mono font-bold text-white/40 uppercase tracking-widest">Status: Absolute Alpha</span>
               </div>
               <div className="text-[9px] font-mono text-white/30 italic">
                 Calculated on {new Date().toISOString()}
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
